/* eslint-disable react/no-multi-comp */
import React, {
  type ForwardedRef,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined'
import TuneIcon from '@mui/icons-material/Tune'
import {
  FormControlLabel,
  Grid,
  InputLabel,
  Radio,
  RadioGroup,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import makeStyles from '@mui/styles/makeStyles'
import moment from 'moment'
import * as R from 'ramda'
import {
  DateFormat,
  EventTypeName,
  LanguageUtils,
  type Nil,
  PermissionArea,
  PuiCheckbox,
  PuiTextArea,
  PuiTextField,
  Text,
  TextWithTooltip,
  useFields,
  Utils,
} from '@pbt/pbt-ui-components'
import { WeekDays } from '@pbt/pbt-ui-components/src/localization'

import PuiSelectAll from '~/components/common/inputs/PuiSelectAll'
import UserSelect from '~/components/common/inputs/UserSelect'
import TimeUnitsSelect from '~/components/common/TimeUnitsSelect'
import { NumberStepper } from '~/components/elements/NumberStepper/NumberStepper'
import FeatureToggle from '~/constants/featureToggle'
import { DEFAULT_APPOINTMENT_NAME } from '~/constants/schedulerConstants'
import { getCRUDByArea } from '~/store/reducers/auth'
import { getFeatureToggle, getTimeUnits } from '~/store/reducers/constants'
import { getUser } from '~/store/reducers/users'
import {
  type DataHandle,
  type ReleasePeriod,
  TeamFilter,
  type TimetableEvent,
} from '~/types'
import { getConstantsList } from '~/utils'
import {
  convertToHours,
  getAppointmentRecurrenceTimeUnitConstant,
  isHoursDivisibleBy24,
  TimeUnits as TimeUnitsEnum,
  TimeUnitsAdjectives,
} from '~/utils/time'
import { useEventType } from '~/utils/useEventType'
import useFieldsChanged, { type FieldCache } from '~/utils/useFieldsChanged'

import { EFFECTIVE_ANY_TIME } from '../../TimetableUtilsNew'
import AppointmentDateSection, {
  type AppointmentDateSectionHandle,
} from './AppointmentDateSection'
import { useGetIsReservedType } from './appointmentUtils'

enum SelfSchedulingStartOption {
  ANY_TIME = 'any_time',
  SPECIFIC_TIME = 'specific_time',
}

const getSelfSchedulingStartOption = (
  availableForSchedulingTimeOffset: ReleasePeriod | Nil,
): SelfSchedulingStartOption => {
  // auto-select "any time" option only if the offset is effectively "any time"
  if (availableForSchedulingTimeOffset === EFFECTIVE_ANY_TIME) {
    return SelfSchedulingStartOption.ANY_TIME
  }
  // if no offset is set (null), default to "specific time" for new appointments
  // for existing appointments, this will be "specific time" unless availableForSchedulingTimeOffset is EFFECTIVE_ANY_TIME
  return SelfSchedulingStartOption.SPECIFIC_TIME
}

const AllowedSelfSchedulingUnits: string[] = [
  LanguageUtils.capitalize(TimeUnitsEnum.DAY),
  LanguageUtils.capitalize(TimeUnitsEnum.HOUR),
]

const AllowedAutomaticReleaseUnits: string[] = [
  LanguageUtils.capitalize(TimeUnitsEnum.HOUR),
  LanguageUtils.capitalize(TimeUnitsEnum.MINUTE),
]
const DEFAULT_SELF_SCHEDULING_INTERVAL = 3

export const getReleaseTimeValue = (releaseTimeOffset: ReleasePeriod | Nil) => {
  if (releaseTimeOffset === EFFECTIVE_ANY_TIME) {
    return undefined // any time does not have a value
  }

  const hoursValue = releaseTimeOffset?.match(/PT(\d+)H/)?.[1]
  if (!hoursValue) return undefined

  const hours = Number(hoursValue)

  if (isHoursDivisibleBy24(hours)) {
    return (hours / 24).toString()
  }

  return hoursValue
}

export const getReleaseTimeConstant = (
  releaseTimeOffset: ReleasePeriod | Nil,
) => {
  if (!releaseTimeOffset) {
    return LanguageUtils.capitalize(TimeUnitsEnum.HOUR)
  }

  const hoursValue = releaseTimeOffset.match(/PT(\d+)H/)?.[1]
  if (hoursValue) {
    const hours = Number(hoursValue)
    if (isHoursDivisibleBy24(hours)) {
      return LanguageUtils.capitalize(TimeUnitsEnum.DAY)
    }
  }

  return LanguageUtils.capitalize(TimeUnitsEnum.HOUR)
}

export const getAutomaticReleaseTimeValue = (
  releaseTimeOffset: ReleasePeriod | Nil,
) => {
  const minutesValue = releaseTimeOffset?.match(/PT(\d+)M/)?.[1]
  if (minutesValue) {
    return minutesValue
  }

  const hoursValue = releaseTimeOffset?.match(/PT(\d+)H/)?.[1]
  if (hoursValue) {
    return hoursValue
  }

  return undefined
}

export const getAutomaticReleaseTimeConstant = (
  releaseTimeOffset: ReleasePeriod | Nil,
) => {
  if (!releaseTimeOffset) {
    return LanguageUtils.capitalize(TimeUnitsEnum.HOUR)
  }

  const minutesValue = releaseTimeOffset.match(/PT(\d+)M/)?.[1]
  if (minutesValue) {
    return LanguageUtils.capitalize(TimeUnitsEnum.MINUTE)
  }

  const hoursValue = releaseTimeOffset.match(/PT(\d+)H/)?.[1]
  if (hoursValue) {
    return LanguageUtils.capitalize(TimeUnitsEnum.HOUR)
  }

  return LanguageUtils.capitalize(TimeUnitsEnum.HOUR)
}

const useStyles = makeStyles(
  (theme) => ({
    root: {
      backgroundColor: theme.colors.tableBackground,
    },
    icon: {
      color: theme.colors.selectedOption,
    },
    checkboxRoot: {
      paddingRight: theme.spacing(0.5),
    },
    checkbox: {
      fontSize: '1.4rem',
    },
  }),
  { name: 'Busy' },
)

const ReadOnlyItemContainer = styled(Grid)`
  max-width: 800px;
`

const ReadOnlyItemRow = styled(Grid)`
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayGray3};
  color: ${({ theme }) => theme.colors.grayGray1};
`
const ReadOnlyItemValue = styled(Grid)`
  color: ${({ theme }) => theme.colors.grayGray1};
`

const ReadOnlyBusyLine = ({
  fieldName,
  fieldValue,
}: {
  fieldName?: string
  fieldValue?: string
}) => (
  <ReadOnlyItemRow container item lineHeight={1.4} py={1} xs={12}>
    <Grid item marginBottom="auto" pl={1} xs={3}>
      <Text strong color="grayGray1" fontSize="1.4rem">
        {fieldName}
      </Text>
    </Grid>
    <ReadOnlyItemValue item xs={9}>
      {fieldValue}
    </ReadOnlyItemValue>
  </ReadOnlyItemRow>
)

const getTimeRangeString = (
  startDatetimeString: string | Nil,
  endDateTimeString: string | Nil,
) => {
  if (!startDatetimeString || !endDateTimeString) {
    return ''
  }
  const startDateString = moment(startDatetimeString).format('MM/DD/YYYY')
  const endDateString = moment(endDateTimeString).format('MM/DD/YYYY')

  const startTimeString = moment(startDatetimeString).format(
    DateFormat.TIME_WITH_MERIDIAN,
  )
  const endTimeString = moment(endDateTimeString).format(
    DateFormat.TIME_WITH_MERIDIAN,
  )

  return `${startDateString} ${startTimeString} - ${endDateString === startDateString ? '' : endDateString} ${endTimeString}`
}

export interface BusyProps {
  appointment: TimetableEvent | Nil
  appointmentTypeId: string
  onFieldsChange?: (changedFields: FieldCache) => void
  personId?: string
}

export interface BusyHandle extends DataHandle {}

const Busy = forwardRef(function Busy(
  { appointment, appointmentTypeId, personId, onFieldsChange = R.F }: BusyProps,
  ref: ForwardedRef<BusyHandle>,
) {
  const classes = useStyles()
  const appointmentPermissions = useSelector(
    getCRUDByArea(PermissionArea.EVENT_APPOINTMENT),
  )
  const { t } = useTranslation([
    'TimeTable',
    'Tooltips',
    'Plurals',
    'Common',
    'Time',
  ])

  const TimeUnits = useSelector(getTimeUnits)
  const SelfSchedulingTimeUnits = TimeUnits.filter((unit) =>
    AllowedSelfSchedulingUnits.includes(unit.name),
  )
  const AutomaticReleaseTimeUnits = TimeUnits.filter((unit) =>
    AllowedAutomaticReleaseUnits.includes(unit.name),
  )

  const isAppointmentReservedEnabled = useSelector(
    getFeatureToggle(FeatureToggle.APPOINTMENT_RESERVED),
  )
  const initialPerson = useSelector(getUser(appointment?.personId || personId))
  const reservedBlockPermissions = useSelector(
    getCRUDByArea(PermissionArea.RESERVED_BLOCK),
  )
  const getIsReservedType = useGetIsReservedType()
  const isReservedAppointment = getIsReservedType(appointmentTypeId)
  const appointmentDateSectionRef = useRef<AppointmentDateSectionHandle>(null)

  const getAllowSelfSchedulingInitialValue = (
    appointmentData: TimetableEvent | Nil,
  ): boolean => {
    // For new appointments (appointment is null/undefined or has no id), default to true
    if (!appointmentData || !appointmentData.id) {
      return true
    }

    // For existing appointments with null availableForSchedulingTimeOffset, default to false
    if (appointmentData.availableForSchedulingTimeOffset === null) {
      return false
    }

    // For existing appointments with a value, use that value
    return Boolean(appointmentData.availableForSchedulingTimeOffset)
  }

  const userHasPermissions =
    !isAppointmentReservedEnabled ||
    !isReservedAppointment ||
    reservedBlockPermissions.create

  const AppointmentSubTypes = useEventType(
    EventTypeName.Appointment,
    'subTypes',
  )

  const getAppointmentTypeObjects = (
    appointmentTypeRestrictionIds?: string[],
  ) => {
    if (
      !appointmentTypeRestrictionIds ||
      appointmentTypeRestrictionIds.length === 0
    ) {
      return []
    }
    return getConstantsList(appointmentTypeRestrictionIds, AppointmentSubTypes)
  }

  const getReadOnlySelfSchedulingStartText = (
    availableForSchedulingTimeOffset: ReleasePeriod | Nil,
  ) => {
    if (!availableForSchedulingTimeOffset) return t('Common:NEVER')
    if (availableForSchedulingTimeOffset === EFFECTIVE_ANY_TIME)
      return t('Common:ANY_TIME')
    return t(
      'Plurals:Z_ICU_WORKAROUND.SCHEDULING_AVAILABILITY.SELF_SCHEDULE_RELEASE_TIME',
      {
        numUnits: Number(getReleaseTimeValue(availableForSchedulingTimeOffset)),
        unit: getReleaseTimeConstant(
          availableForSchedulingTimeOffset,
        ).toLocaleLowerCase(),
      },
    )
  }
  const removeAutomaticallyInitialValue = useMemo(
    () =>
      appointment?.releaseTimeOffset !== undefined
        ? Boolean(appointment?.releaseTimeOffset)
        : true,
    [appointment],
  )

  const { fields, validate } = useFields(
    [
      {
        name: 'title',
        initialValue:
          !appointment?.name || appointment?.name === DEFAULT_APPOINTMENT_NAME
            ? ''
            : appointment?.name,
      },
      {
        name: 'person',
        label: t('Common:TEAM_MEMBER'),
        initialValue: appointment?.personId || personId || '',
      },
      { name: 'notes', initialValue: appointment?.notes || '' },
      {
        name: 'appointmentTypeIds',
        label: t('Common:APPOINTMENT_TYPES'),
        type: 'select',
        initialValue: getAppointmentTypeObjects(
          appointment?.appointmentTypeRestrictionIds,
        ),
      },
      {
        name: 'memberOnly',
        label: t('Common:MEMBER_ONLY'),
        type: 'toggle',
        initialValue: Boolean(appointment?.wplanMembersOnly),
      },
      {
        name: 'intervalRelease',
        type: 'select',
        initialValue: Utils.findConstantIdByName(
          LanguageUtils.capitalize(
            getAutomaticReleaseTimeConstant(appointment?.releaseTimeOffset),
          ),
          AutomaticReleaseTimeUnits,
        ),
      },
      {
        name: 'numberOfIntervalsRelease',
        initialValue: getAutomaticReleaseTimeValue(
          appointment?.releaseTimeOffset,
        )
          ? Number(getAutomaticReleaseTimeValue(appointment?.releaseTimeOffset))
          : 2,
      },
      {
        name: 'intervalReleaseToSchedule',
        type: 'select',
        initialValue: Utils.findConstantIdByName(
          LanguageUtils.capitalize(
            appointment?.availableForSchedulingTimeOffset
              ? getReleaseTimeConstant(
                  appointment.availableForSchedulingTimeOffset,
                )
              : 'Day',
          ),
          SelfSchedulingTimeUnits,
        ),
      },
      {
        name: 'numberOfIntervalsReleaseToSchedule',
        initialValue: getReleaseTimeValue(
          appointment?.availableForSchedulingTimeOffset,
        )
          ? Number(
              getReleaseTimeValue(
                appointment?.availableForSchedulingTimeOffset,
              ),
            )
          : DEFAULT_SELF_SCHEDULING_INTERVAL,
      },
      {
        name: 'selfSchedulingStartSelection',
        initialValue: getSelfSchedulingStartOption(
          appointment?.availableForSchedulingTimeOffset,
        ),
        type: 'select',
      },
      {
        name: 'allowSelfScheduling',
        initialValue: getAllowSelfSchedulingInitialValue(appointment),
        type: 'toggle',
      },
      {
        name: 'removeAutomatically',
        initialValue: removeAutomaticallyInitialValue,
        type: 'toggle',
      },
    ],
    false,
  )

  const {
    title,
    person,
    notes,
    appointmentTypeIds,
    memberOnly,
    intervalRelease,
    numberOfIntervalsRelease,
    intervalReleaseToSchedule,
    numberOfIntervalsReleaseToSchedule,
    selfSchedulingStartSelection,
    allowSelfScheduling,
    removeAutomatically,
  } = fields

  useFieldsChanged(onFieldsChange, fields)

  useImperativeHandle(ref, () => ({
    validate: () =>
      validate() && (appointmentDateSectionRef.current?.validate() ?? true),
    get: () => {
      const data = {
        type: appointmentTypeId,
        name: title.value || DEFAULT_APPOINTMENT_NAME,
        person: person.value || null,
        notes: notes.value,
        wplanMembersOnly: memberOnly.value,
        appointmentTypeRestrictionIds: appointmentTypeIds.value.map(
          (appointmentType: { id: string }) => appointmentType.id,
        ),
        ...appointmentDateSectionRef.current?.get(),
      }

      if (isAppointmentReservedEnabled) {
        if (removeAutomatically.value) {
          const releaseUnit = Utils.getConstantName(
            intervalRelease.value,
            AutomaticReleaseTimeUnits,
          )

          // For minutes, use PTXM format; for hours, use PTXH format
          if (releaseUnit === LanguageUtils.capitalize(TimeUnitsEnum.MINUTE)) {
            data.releaseTimeOffset = `PT${numberOfIntervalsRelease.value}M`
          } else {
            const hoursValue = Math.round(
              convertToHours(numberOfIntervalsRelease.value, releaseUnit),
            )
            data.releaseTimeOffset = `PT${hoursValue}H`
          }
        }
        if (allowSelfScheduling.value) {
          const releaseUnit = Utils.getConstantName(
            intervalReleaseToSchedule.value,
            SelfSchedulingTimeUnits,
          )
          switch (selfSchedulingStartSelection.value) {
            case SelfSchedulingStartOption.SPECIFIC_TIME:
              const hoursValue = Math.round(
                convertToHours(
                  numberOfIntervalsReleaseToSchedule.value,
                  releaseUnit,
                ),
              )
              data.availableForSchedulingTimeOffset = `PT${hoursValue}H`
              break
            default:
              data.availableForSchedulingTimeOffset = EFFECTIVE_ANY_TIME
              break
          }
        }
      }

      return data
    },
  }))

  return (
    <Grid container item className={classes.root} pb={2} px={3} rowSpacing={1}>
      {userHasPermissions ? (
        <>
          <Grid item xs={12}>
            <Grid
              container
              item
              alignItems="center"
              columnSpacing={1}
              wrap="nowrap"
              xs={5}
            >
              <Grid item mt={3}>
                <TuneIcon className={classes.icon} />
              </Grid>
              <Grid item xs>
                <PuiTextField
                  disabled={!appointmentPermissions.update}
                  field={title}
                  label={t('Common:TITLE')}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid
              container
              item
              alignItems="center"
              columnSpacing={1}
              wrap="nowrap"
              xs={3}
            >
              <Grid item mt={1}>
                <PersonOutlinedIcon className={classes.icon} />
              </Grid>
              <Grid item xs>
                <UserSelect
                  disabled={!appointmentPermissions.update}
                  field={person}
                  label={person.label}
                  teamFilter={TeamFilter.ALL}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid item xs={8}>
              <AppointmentDateSection
                appointment={appointment}
                appointmentTypeId={appointmentTypeId}
                ref={appointmentDateSectionRef}
                onFieldsChange={onFieldsChange}
              />
            </Grid>
          </Grid>
        </>
      ) : (
        <>
          <Grid item xs={12}>
            <ReadOnlyItemContainer container item pt={3}>
              <ReadOnlyBusyLine
                fieldName={t('Common:TITLE')}
                fieldValue={appointment?.name}
              />
              <ReadOnlyBusyLine
                fieldName={t('Common:ASSIGNED')}
                fieldValue={Utils.getPersonString(initialPerson)}
              />
              <ReadOnlyBusyLine
                fieldName={t('Common:WHEN')}
                fieldValue={getTimeRangeString(
                  appointment?.scheduledStartDatetime,
                  appointment?.scheduledEndDatetime,
                )}
              />
              <ReadOnlyBusyLine
                fieldName={t('Common:REPEAT_ACTION')}
                fieldValue={
                  appointment?.recurrenceParams
                    ? `${
                        TimeUnitsAdjectives[
                          getAppointmentRecurrenceTimeUnitConstant(appointment)
                        ]
                      }; ${appointment?.recurrenceParams?.days
                        .map((day) => WeekDays[day])
                        .join(', ')}`
                    : t('Common:NO_REPEAT')
                }
              />
              <ReadOnlyBusyLine
                fieldName={t('Common:RELEASE_AUTOMATICALLY')}
                fieldValue={
                  appointment?.releaseTimeOffset
                    ? t('Plurals:Z_ICU_WORKAROUND.RELEASE_TIME', {
                        numUnits: Number(
                          getAutomaticReleaseTimeValue(
                            appointment?.releaseTimeOffset,
                          ),
                        ),
                        unit:
                          getAutomaticReleaseTimeConstant(
                            appointment?.releaseTimeOffset,
                          ).toLocaleLowerCase() || '',
                      })
                    : t('Common:NO')
                }
              />
            </ReadOnlyItemContainer>
          </Grid>
        </>
      )}
      {isAppointmentReservedEnabled && isReservedAppointment && (
        <Grid item xs={12}>
          <Grid item xs={6}>
            <TextWithTooltip
              strong
              tooltipText={t('Tooltips:RESERVED_RESTRICTION')}
              variant="subheading3"
            >
              {t('Common:RESTRICTION_OTHER')}
            </TextWithTooltip>
          </Grid>
          <Grid item xs={12}>
            {userHasPermissions ? (
              <PuiSelectAll
                hideLabelWhenEmpty
                field={appointmentTypeIds}
                items={AppointmentSubTypes}
                label={appointmentTypeIds.label}
                noneSelectedText={t('Common:APPOINTMENT_TYPES')}
              />
            ) : (
              <ReadOnlyItemContainer container item pt={1}>
                <ReadOnlyBusyLine
                  fieldName={appointmentTypeIds.label}
                  fieldValue={
                    getAppointmentTypeObjects(
                      appointment?.appointmentTypeRestrictionIds,
                    )
                      .map((at) => at.name)
                      .join(', ') ||
                    t('TimeTable:ADD_APPOINTMENT_COMPONENT.NO_RESTRICTIONS')
                  }
                />
                <ReadOnlyBusyLine
                  fieldName={t('Common:MEMBER_ONLY')}
                  fieldValue={
                    appointment?.wplanMembersOnly
                      ? t('Common:TRUE')
                      : t('TimeTable:ADD_APPOINTMENT_COMPONENT.NO_RESTRICTIONS')
                  }
                />
              </ReadOnlyItemContainer>
            )}
          </Grid>
        </Grid>
      )}
      {isAppointmentReservedEnabled &&
        isReservedAppointment &&
        userHasPermissions && (
          <Grid item xs={12}>
            <Grid item xs={6}>
              <PuiCheckbox
                checkboxClasses={{
                  root: classes.checkboxRoot,
                }}
                className={classes.checkbox}
                field={memberOnly}
                label={t('Common:MEMBER_ONLY')}
                name="memberOnly"
              />
            </Grid>
          </Grid>
        )}
      {isAppointmentReservedEnabled && isReservedAppointment && (
        <Grid item xs={12}>
          <Grid item xs={12}>
            <Text strong variant="subheading3">
              {t('Common:SCHEDULING_AVAILABILITY')}
            </Text>
          </Grid>
          {userHasPermissions ? (
            <Grid container item direction="column" wrap="nowrap">
              <PuiCheckbox
                checkboxClasses={{
                  root: classes.checkboxRoot,
                }}
                className={classes.checkbox}
                field={allowSelfScheduling}
                label={t('Common:ALLOW_SELF_SCHEDULING_LABEL')}
                name="allowSelfScheduling"
              />
              {allowSelfScheduling.value && (
                <Grid container item direction="column" pl={4}>
                  <Grid container item alignItems="center">
                    <RadioGroup
                      value={selfSchedulingStartSelection.value}
                      onChange={selfSchedulingStartSelection.set}
                    >
                      <FormControlLabel
                        control={<Radio />}
                        label={t('Common:ANY_TIME')}
                        value={SelfSchedulingStartOption.ANY_TIME}
                      />
                      <FormControlLabel
                        control={<Radio />}
                        label={
                          <Grid container item alignItems="center">
                            <Text variant="body2">{t('Common:STARTING')}</Text>
                            <Grid item px={1}>
                              <NumberStepper
                                disabled={
                                  selfSchedulingStartSelection.value !==
                                  SelfSchedulingStartOption.SPECIFIC_TIME
                                }
                                field={numberOfIntervalsReleaseToSchedule}
                              />
                            </Grid>
                            <TimeUnitsSelect
                              amount={numberOfIntervalsReleaseToSchedule.value}
                              disabled={
                                selfSchedulingStartSelection.value !==
                                SelfSchedulingStartOption.SPECIFIC_TIME
                              }
                              field={intervalReleaseToSchedule}
                              items={SelfSchedulingTimeUnits}
                              renderEmpty={false}
                            />
                            <Text pl={1} variant="body2">
                              {t('Common:BEFORE_RESERVED_TIME')}
                            </Text>
                          </Grid>
                        }
                        value={SelfSchedulingStartOption.SPECIFIC_TIME}
                      />
                    </RadioGroup>
                  </Grid>
                </Grid>
              )}
              <PuiCheckbox
                checkboxClasses={{
                  root: classes.checkboxRoot,
                }}
                className={classes.checkbox}
                field={removeAutomatically}
                label={t('Time:LABEL.REMOVE_AUTOMATICALLY')}
                name="removeAutomatically"
              />
              {removeAutomatically.value && (
                <Grid container item direction="column" pl={4}>
                  <Grid container item alignItems="center">
                    <Text variant="body2">{t('Common:RELEASE')}</Text>
                    <Grid item px={1}>
                      <NumberStepper field={numberOfIntervalsRelease} />
                    </Grid>
                    <TimeUnitsSelect
                      amount={numberOfIntervalsRelease.value}
                      field={intervalRelease}
                      items={AutomaticReleaseTimeUnits}
                      renderEmpty={false}
                    />
                    <Text pl={1} variant="body2">
                      {t('Common:BEFORE_RESERVED_TIME')}
                    </Text>
                  </Grid>
                </Grid>
              )}
            </Grid>
          ) : (
            <ReadOnlyItemContainer container item pt={1}>
              <ReadOnlyBusyLine
                fieldName={t('Common:PET_PARENTS_CAN_SELF_SCHEDULE')}
                fieldValue={getReadOnlySelfSchedulingStartText(
                  appointment?.availableForSchedulingTimeOffset,
                )}
              />
              <ReadOnlyBusyLine
                fieldName={t('Common:AUTOMATICALLY_RELEASE_RESERVED_BLOCK')}
                fieldValue={
                  appointment?.releaseTimeOffset
                    ? t(
                        'Plurals:Z_ICU_WORKAROUND.SCHEDULING_AVAILABILITY.AUTOMATICALLY_RELEASE_RESERVED_BLOCK',
                        {
                          numUnits: Number(
                            getAutomaticReleaseTimeValue(
                              appointment?.releaseTimeOffset,
                            ),
                          ),
                          unit: getAutomaticReleaseTimeConstant(
                            appointment.releaseTimeOffset,
                          ).toLocaleLowerCase(),
                        },
                      )
                    : t('Common:NEVER')
                }
              />
            </ReadOnlyItemContainer>
          )}
        </Grid>
      )}
      {(userHasPermissions || appointment?.notes) && (
        <Grid item mt={1} xs={12}>
          <InputLabel htmlFor="notes-input">
            <Text strong variant="subheading3">
              {t('Common:NOTES')}
            </Text>
          </InputLabel>
          {userHasPermissions ? (
            <PuiTextArea
              multiline
              disabled={!appointmentPermissions.update}
              field={notes}
              id="notes-input"
              margin="none"
              maxRows={6}
              minRows={1}
            />
          ) : (
            appointment?.notes && (
              <Text pl={1} pt={1}>
                {appointment?.notes}
              </Text>
            )
          )}
        </Grid>
      )}
    </Grid>
  )
})

export default Busy
